// Rascunho local do checklist em andamento.
//
// PROBLEMA QUE ISTO RESOLVE
// Ate aqui, um checklist sendo preenchido existia apenas no estado do React.
// Tablet que dorme, aba fechada sem querer, recarregamento, queda de conexao
// ou um save recusado pelo servidor: o trabalho inteiro desaparecia sem
// rastro. Este modulo guarda uma copia local enquanto o preenchimento
// acontece, e a devolve na proxima abertura -- sempre PERGUNTANDO, nunca
// sobrescrevendo nada sozinho.
//
// POR QUE IndexedDB E NAO localStorage
// Medicao feita em producao (saas-gl.duckdns.org, 14/09/2026): o
// localStorage desta origem ja ocupa ~4,4 MB em 75 chaves -- caches do
// painel de gestao (saas_config, saas_tema, os_cache_*, clientes_cache_*).
// A cota tipica e de 5 MB. Um checklist com fotos pesa ~1,5 MB. Gravar o
// rascunho ali estouraria a cota, e o efeito colateral cairia sobre os
// caches do PAINEL DE GESTAO -- que esta explicitamente fora de escopo.
// O IndexedDB tem cota propria e muito maior, e vive isolado nesta base.
//
// GARANTIAS
// - Nao le, nao escreve e nao apaga nada fora desta base de dados.
// - Nao fala com o servidor. Nao toca em nenhuma tabela.
// - Isolamento por tenant: a chave sempre comeca pelo tenantId.
// - Se o IndexedDB nao existir ou falhar (aba anonima, cota, permissao),
//   toda funcao devolve um valor neutro e o formulario segue como antes.
//   O rascunho e uma rede de protecao: ele nunca pode derrubar a tela.

const BASE = "saasgl_checklist_rascunhos";
const LOJA = "rascunhos";
const VERSAO_BASE = 1;
// Versao do formato do registro. Se um dia o formato mudar, um rascunho
// antigo e ignorado em vez de restaurado torto.
const VERSAO_FORMATO = 1;

let promessaBase = null;

function abrir() {
  if (promessaBase) return promessaBase;
  promessaBase = new Promise((resolve) => {
    try {
      if (typeof indexedDB === "undefined") return resolve(null);
      const req = indexedDB.open(BASE, VERSAO_BASE);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(LOJA)) {
          db.createObjectStore(LOJA, { keyPath: "chave" });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch (e) {
      resolve(null);
    }
  });
  return promessaBase;
}

function chaveDe(tenantId, checklistId) {
  return String(tenantId == null ? "" : tenantId) + ":" + String(checklistId == null ? "" : checklistId);
}

function transacao(db, modo, executar) {
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(LOJA, modo);
      const loja = tx.objectStore(LOJA);
      let resultado = null;
      const req = executar(loja);
      if (req) req.onsuccess = () => { resultado = req.result; };
      tx.oncomplete = () => resolve(resultado);
      tx.onerror = () => resolve(null);
      tx.onabort = () => resolve(null);
    } catch (e) {
      resolve(null);
    }
  });
}

// Grava (ou regrava) o rascunho deste checklist. Devolve true se gravou.
export async function salvarRascunho(tenantId, checklist) {
  if (!checklist || !checklist.id) return false;
  const db = await abrir();
  if (!db) return false;
  const registro = {
    chave: chaveDe(tenantId, checklist.id),
    tenantId: String(tenantId == null ? "" : tenantId),
    checklistId: String(checklist.id),
    versaoFormato: VERSAO_FORMATO,
    salvoEm: new Date().toISOString(),
    dados: checklist,
  };
  const r = await transacao(db, "readwrite", (loja) => loja.put(registro));
  return r !== null;
}

// Le o rascunho deste checklist. Devolve null se nao houver, se o formato
// for de outra versao, ou se o IndexedDB nao estiver disponivel.
export async function lerRascunho(tenantId, checklistId) {
  if (!checklistId) return null;
  const db = await abrir();
  if (!db) return null;
  const reg = await transacao(db, "readonly", (loja) => loja.get(chaveDe(tenantId, checklistId)));
  if (!reg || reg.versaoFormato !== VERSAO_FORMATO || !reg.dados) return null;
  // Cinto de seguranca contra chave de outro tenant: a chave ja isola, mas
  // conferir aqui tambem custa nada.
  if (reg.tenantId !== String(tenantId == null ? "" : tenantId)) return null;
  return { dados: reg.dados, salvoEm: reg.salvoEm };
}

// Remove o rascunho. Usado em dois momentos, ambos seguros:
// 1) o checklist foi gravado no servidor com sucesso -- a copia local
//    perdeu a funcao, o dado esta no banco;
// 2) a pessoa clicou em "Descartar" no aviso de restauracao.
// Nao remove em nenhuma outra situacao.
export async function apagarRascunho(tenantId, checklistId) {
  if (!checklistId) return false;
  const db = await abrir();
  if (!db) return false;
  const r = await transacao(db, "readwrite", (loja) => loja.delete(chaveDe(tenantId, checklistId)));
  return r !== null;
}

// Formata "salvoEm" para o aviso na tela, em portugues e sem depender de
// biblioteca de datas.
export function quandoFoi(iso) {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const agora = new Date();
    const minutos = Math.floor((agora - d) / 60000);
    if (minutos < 1) return "agora mesmo";
    if (minutos < 60) return "ha " + minutos + (minutos === 1 ? " minuto" : " minutos");
    const horas = Math.floor(minutos / 60);
    if (horas < 24) return "ha " + horas + (horas === 1 ? " hora" : " horas");
    return "em " + d.toLocaleDateString("pt-BR") + " as " + d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  } catch (e) {
    return "";
  }
}
